// Live camera capture for the identity check. Photos are never picked from
// the gallery: the student has to take them on the spot (selfie with the
// front camera, document with the back one), which is the whole point of the
// check. Pure helpers live here so they can be unit-tested without a browser.

import { PHOTO_MIME, fitWithin, stripDataUrl } from './image-resize';

export type PhotoKind = 'selfie' | 'id';
export type Facing = 'user' | 'environment';

export type CameraErrorKind = 'denied' | 'no-camera' | 'busy' | 'unsupported' | 'unknown';

/** Which camera each photo kind starts on. */
export function defaultFacingFor(kind: PhotoKind): Facing {
  return kind === 'selfie' ? 'user' : 'environment';
}

export function flipFacing(facing: Facing): Facing {
  return facing === 'user' ? 'environment' : 'user';
}

/**
 * getUserMedia constraints. The selfie asks for the front camera outright;
 * documents prefer the back one but accept whatever exists (laptops only have
 * a front camera). Video only — never audio.
 */
export function constraintsFor(facing: Facing): MediaStreamConstraints {
  return {
    audio: false,
    video: {
      facingMode: facing === 'user' ? 'user' : { ideal: 'environment' },
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
  };
}

/** The dashed guide drawn over the preview: a face oval or a card rectangle. */
export function overlayFor(kind: PhotoKind): 'oval' | 'card' {
  return kind === 'selfie' ? 'oval' : 'card';
}

/** The selfie preview is mirrored so it behaves like a mirror; documents never are. */
export function shouldMirrorPreview(facing: Facing): boolean {
  return facing === 'user';
}

/** True when the browser can open a camera at all (false on old browsers / plain http). */
export function isCameraSupported(nav: Pick<Navigator, 'mediaDevices'> | undefined = globalThis.navigator): boolean {
  return Boolean(nav?.mediaDevices && typeof nav.mediaDevices.getUserMedia === 'function');
}

/**
 * Maps the DOMException names browsers throw from getUserMedia onto the few
 * situations the screen explains differently.
 */
export function mapCameraError(error: unknown): CameraErrorKind {
  const name = (error as { name?: string } | null)?.name ?? '';
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return 'no-camera';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'busy';
    case 'TypeError':
      return 'unsupported';
    default:
      return 'unknown';
  }
}

export function stopStream(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach((track) => {
    try {
      track.stop();
    } catch {
      // A track that is already ended throws on some browsers; nothing to do.
    }
  });
}

/**
 * Snapshot of the current video frame as a JPEG (longest edge ≤ 1600px).
 * `mirror` flips the frame horizontally — used for selfies so the saved photo
 * matches what the student saw; documents are captured as-is so text stays
 * readable.
 */
export function captureFrame(
  video: HTMLVideoElement,
  opts: { mirror: boolean; quality?: number } = { mirror: false },
): { imageBase64: string; mimeType: typeof PHOTO_MIME; previewUrl: string } {
  const sourceWidth = video.videoWidth;
  const sourceHeight = video.videoHeight;
  if (!sourceWidth || !sourceHeight) throw new Error('video-not-ready');
  const { width, height } = fitWithin(sourceWidth, sourceHeight);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas-unavailable');
  if (opts.mirror) {
    ctx.translate(width, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(video, 0, 0, width, height);
  const previewUrl = canvas.toDataURL(PHOTO_MIME, opts.quality ?? 0.85);
  return { imageBase64: stripDataUrl(previewUrl), mimeType: PHOTO_MIME, previewUrl };
}
